import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Fetch kind 10003 bookmarks list for current signed-in user
 */
export function useBookmarks() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['bookmarks', user?.pubkey],
    queryFn: async (): Promise<{ event: NostrEvent | null; bookmarkedIds: Set<string> }> => {
      const defaultVal = { event: null as NostrEvent | null, bookmarkedIds: new Set<string>() };
      if (!nostr || !user) return defaultVal;

      try {
        const events = await nostr.query([{ kinds: [10003], authors: [user.pubkey], limit: 1 }]);
        if (events[0]) {
          const list = events[0].tags.filter(t => t[0] === 'e').map(t => t[1]);
          return {
            event: events[0],
            bookmarkedIds: new Set(list)
          };
        }
      } catch (err) {
        console.error('Failed to query NIP-51 kind-10003 bookmarks list:', err);
      }

      return defaultVal;
    },
    enabled: !!nostr,
  });
}

/**
 * Mutation to toggle bookmarks on/off (highly compliant chronological append NIP-51)
 */
export function useToggleBookmark() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const queryClient = useQueryClient();
  const { data: bookmarksData } = useBookmarks();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!nostr || !user) throw new Error('Sign-in required to bookmarks posts');

      const existingIds = bookmarksData?.bookmarkedIds ?? new Set<string>();
      const existingRelayTags = bookmarksData?.event?.tags ?? [];

      let newTags: string[][] = [];

      if (existingIds.has(noteId)) {
        // Un-bookmark: filter out the targeted noteId's e tag
        newTags = existingRelayTags.filter(t => !(t[0] === 'e' && t[1] === noteId));
      } else {
        // Bookmark: chronologically append 'e' tag to the end per NIP-51
        newTags = [...existingRelayTags, ['e', noteId]];
      }

      // Preserve non-e tags (like d, t, etc.)
      const otherTags = existingRelayTags.filter(t => t[0] !== 'e');
      const eTags = newTags.filter(t => t[0] === 'e');
      const finalTags = [...otherTags, ...eTags];

      // Publish new kind 10003 replaceable list
      await publish({
        kind: 10003,
        content: '',
        tags: finalTags
      });

      return { noteId, added: !existingIds.has(noteId) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks', user?.pubkey] });
    }
  });
}
